import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Base URL for the website
const BASE_URL = 'https://www.therlkgroup.com';

// SEO metadata for each page - Updated for 2026 repositioning with enhanced product keywords
const seoData = {
  '/': {
    title: 'The RLK Group | Operational Intelligence & Autonomy Technologies',
    description: 'The RLK Group develops operational intelligence, mission software and autonomy technologies for defence, public safety and critical infrastructure. UK-based specialists in simulation, mission orchestration and vision-based guidance.',
    keywords: 'The RLK Group, operational intelligence, mission software, autonomy technologies, defence technology, public safety, critical infrastructure, UK, autonomous systems, counter-UAS',
    ogType: 'website',
  },
  
  // Solutions - Enhanced keywords for discoverability
  '/solutions': {
    title: 'Solutions | Defence, Security & Operational Intelligence | The RLK Group',
    description: 'Defined customer outcomes across defence, protective security, public safety and civil operational intelligence. Counter-UAS capability assessment, mission orchestration, and analytics solutions.',
    keywords: 'defence solutions, counter-UAS solutions, protective security solutions, public safety technology, operational intelligence platform, mission systems UK, drone defence',
    ogType: 'website',
  },
  '/solutions/defence-cuas': {
    title: 'Counter-UAS Solutions & Defence Technology | C-UAS Systems | The RLK Group',
    description: 'Counter-UAS capability assessment, mission integration, and autonomous interceptor technology. UAVSimNet simulation for C-UAS evaluation. Hardware-neutral mission systems for drone defence.',
    keywords: 'counter-UAS, C-UAS systems, drone defence, UAV interceptor, anti-drone technology, counter drone, UAS defeat, drone detection, C-UAS UK, counter-UAS simulation, drone threat assessment',
    ogType: 'website',
  },
  '/solutions/protective-security': {
    title: 'Drone Security for Critical Sites | Helais Sentinel | Perimeter Protection',
    description: 'Helais Sentinel drone security system for critical infrastructure and site protection. Aerial observation integrates with existing CCTV and perimeter security. Operator-controlled response.',
    keywords: 'drone security, perimeter security, site protection, critical infrastructure protection, aerial surveillance, security drone system, CCTV integration, Helais Sentinel, protective security UK',
    ogType: 'website',
  },
  '/solutions/public-safety': {
    title: 'Drone Technology for Emergency Services | Public Safety Drones | The RLK Group',
    description: 'Mission orchestration platform for police, fire and emergency response. Multi-drone coordination, incident workflow integration, and BVLOS-ready aerial support systems.',
    keywords: 'public safety drones, police drone, emergency response drone, first responder UAV, fire service drone, search and rescue drone, BVLOS operations, drone as first responder, DFR UK',
    ogType: 'website',
  },
  '/solutions/civil-intelligence': {
    title: 'Video Analytics & Operational Intelligence | Smart City Analytics | The RLK Group',
    description: 'AI video analytics for transport, retail, and public spaces. Convert existing CCTV into operational intelligence. Footfall analytics, queue monitoring, crowd density alerts.',
    keywords: 'video analytics, AI analytics, smart city, footfall analytics, crowd monitoring, queue analytics, transport analytics, CCTV analytics, operational intelligence, urban analytics UK',
    ogType: 'website',
  },
  
  // Platforms - Heavily enhanced for product discoverability
  '/platforms': {
    title: 'Autonomous Systems & Mission Software Platforms | The RLK Group',
    description: 'Autonomous guidance systems, mission orchestration platforms, counter-UAS simulation, and operational analytics. AI-AV, Helais, UAVSimNet, and UrbanAnalytix technology platforms.',
    keywords: 'autonomous systems, mission software, drone platforms, UAV technology, autonomous guidance, mission orchestration platform, counter-UAS simulation, video analytics platform',
    ogType: 'website',
  },
  '/platforms/ai-av': {
    title: 'AI-AV | GPS-Denied Autonomous Navigation | Vision-Based Guidance System',
    description: 'AI-AV vision-based autonomous guidance for UAVs and unmanned platforms. GPS-denied navigation using computer vision. Target tracking and pursuit for interceptor drones and autonomous vehicles.',
    keywords: 'GPS-denied navigation, vision-based guidance, autonomous navigation, visual guidance system, GPS-free drone, computer vision navigation, target tracking, autonomous pursuit, interceptor guidance, AI-AV, visual odometry, GNSS-denied, GPS jamming resistant',
    ogType: 'product',
    additionalMeta: {
      'product:brand': 'The RLK Group',
      'product:category': 'Autonomous Guidance Systems',
      'product:availability': 'available for integration'
    }
  },
  '/platforms/helais': {
    title: 'Helais | Drone Fleet Management & Mission Orchestration Platform',
    description: 'Helais drone fleet management and mission orchestration for security, emergency response, and aerial operations. Multi-drone coordination, dispatch integration, and asset management.',
    keywords: 'drone fleet management, mission orchestration, drone dispatch, UAV coordination, drone operations platform, aerial operations management, multi-drone control, Helais, drone management software, UAS fleet, enterprise drone platform',
    ogType: 'product',
    additionalMeta: {
      'product:brand': 'The RLK Group',
      'product:category': 'Mission Orchestration Platforms',
      'product:availability': 'available for deployment'
    }
  },
  '/platforms/advanced-development': {
    title: 'TALON Interceptor Drone | Counter-UAS Development | The RLK Group',
    description: 'TALON fixed-wing counter-UAS interceptor development programme. Autonomous interceptor drone design, optical-RF sensor fusion, and multi-agent coordination research.',
    keywords: 'TALON interceptor, counter-UAS interceptor, drone interceptor, autonomous interceptor, C-UAS drone, kamikaze drone defence, interceptor UAV, sensor fusion, drone development UK',
    ogType: 'website',
  },
  
  // Company
  '/company': {
    title: 'About The RLK Group | UK Defence & Autonomy Technology Company',
    description: 'The RLK Group: UK defence technology company specialising in autonomous systems, mission software, and operational intelligence. Based in Glasgow, Scotland. Counter-UAS, drone operations, analytics.',
    keywords: 'RLK Group, UK defence company, autonomy company UK, drone technology company, Glasgow technology, Scottish defence, autonomous systems company, mission software UK',
    ogType: 'website',
  },
  '/company/structure': {
    title: 'Our Companies | Group Structure | The RLK Group',
    description: 'The RLK Group operates through focused divisions: RLK Technology Ltd, Helais Ltd, and UrbanAnalytix Ltd. Master brand and operating companies.',
    keywords: 'RLK Technology, Helais Ltd, UrbanAnalytix Ltd, company structure, group companies',
    ogType: 'website',
  },
  '/company/intellectual-property': {
    title: 'Intellectual Property | Patents & Trademarks | The RLK Group',
    description: 'The RLK Group intellectual property portfolio including filed patent applications, registered trademarks, and registered designs for autonomous systems technology.',
    keywords: 'RLK patents, trademarks, intellectual property, AI-AV patent, Helais trademark, autonomous systems IP',
    ogType: 'website',
  },
  
  // Evidence
  '/evidence': {
    title: 'Evidence & Assurance | Autonomous System Validation | The RLK Group',
    description: 'Rigorous evidence and validation for autonomous systems. Model provenance, test documentation, maturity indicators. Building trust through discipline, not marketing claims.',
    keywords: 'autonomous system validation, evidence-based autonomy, model provenance, system testing, maturity indicators, autonomy assurance, test evidence, V&V autonomous',
    ogType: 'website',
  },
  
  // Insights - Enhanced for blog SEO
  '/insights': {
    title: 'Insights | Counter-UAS, Autonomy & Defence Technology Blog | The RLK Group',
    description: 'Technical insights on counter-UAS systems, GPS-denied navigation, drone fleet management, and operational analytics. Expert analysis from The RLK Group.',
    keywords: 'counter-UAS blog, autonomous systems insights, defence technology blog, drone technology articles, GPS-denied navigation, C-UAS analysis, autonomy technology news',
    ogType: 'blog',
  },
  '/contact': {
    title: 'Contact | Request a Counter-UAS or Autonomy Briefing | The RLK Group',
    description: 'Contact The RLK Group for counter-UAS capability assessment, autonomous guidance integration, mission orchestration, or analytics solutions. Glasgow, Scotland.',
    keywords: 'contact RLK, counter-UAS enquiry, autonomy briefing, defence consultation, Glasgow defence company, UK autonomy',
    ogType: 'website',
  },
};

// Organization schema (JSON-LD) - Updated
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "The RLK Group",
  "alternateName": ["RLK Group", "RLK Technology", "Helais", "UrbanAnalytix"],
  "url": BASE_URL,
  "logo": {
    "@type": "ImageObject",
    "url": "https://customer-assets.emergentagent.com/job_rlk-modern/artifacts/wepugaac_RLK%20Grey%20group.png",
    "width": 400,
    "height": 100
  },
  "description": "The RLK Group develops operational intelligence, mission software and autonomy technologies for defence, public safety and critical infrastructure.",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Glasgow",
    "addressRegion": "Scotland",
    "addressCountry": "GB"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+44-141-319-4813",
    "email": "info@rlktech.co.uk",
    "contactType": "customer service",
    "availableLanguage": "English"
  },
  "sameAs": [
    "https://www.linkedin.com/company/the-rlk-group/",
    "https://c-uas.co.uk",
    "https://urban-analytix.co.uk"
  ],
  "foundingDate": "2022",
  "foundingLocation": {
    "@type": "Place",
    "name": "Glasgow, Scotland, UK"
  },
  "numberOfEmployees": {
    "@type": "QuantitativeValue",
    "minValue": 10,
    "maxValue": 50
  },
  "knowsAbout": [
    "Operational Intelligence",
    "Mission Software",
    "Autonomy Technologies",
    "Defence Technology",
    "Counter-UAS Systems",
    "Vision-Based Guidance",
    "Mission Orchestration",
    "Simulation & Decision Support",
    "Urban Analytics"
  ],
  "areaServed": {
    "@type": "GeoCircle",
    "geoMidpoint": {
      "@type": "GeoCoordinates",
      "latitude": 55.8642,
      "longitude": -4.2518
    },
    "geoRadius": "10000"
  }
};

// Website schema
const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "The RLK Group",
  "alternateName": "RLK Group",
  "url": BASE_URL,
  "description": "Operational intelligence, mission software and autonomy technologies for defence, public safety and critical infrastructure",
  "publisher": {
    "@type": "Organization",
    "name": "The RLK Group",
    "url": BASE_URL
  },
  "inLanguage": "en-GB",
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${BASE_URL}/search?q={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  }
};

// Product schemas for main platforms - Enhanced for better SEO
const productSchemas = {
  '/platforms/ai-av': {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "AI-AV Vision-Based Autonomous Guidance System",
    "alternateName": ["AI-AV", "AIAV", "AI-AV Guidance"],
    "description": "Vision-based target-oriented adaptive control for autonomous platforms. GPS-denied navigation using computer vision for UAVs, ground vehicles, and marine platforms. Designed to operate without satellite positioning for the relevant guidance function.",
    "brand": {
      "@type": "Brand",
      "name": "The RLK Group"
    },
    "manufacturer": {
      "@type": "Organization",
      "name": "The RLK Group",
      "url": BASE_URL
    },
    "category": "Autonomous Guidance Systems",
    "additionalType": "https://schema.org/SoftwareApplication",
    "applicationCategory": "Autonomous Navigation Software",
    "operatingSystem": "Embedded Linux",
    "url": `${BASE_URL}/platforms/ai-av`,
    "image": "https://customer-assets.emergentagent.com/job_rlk-modern/artifacts/wepugaac_RLK%20Grey%20group.png",
    "keywords": "GPS-denied navigation, vision-based guidance, autonomous navigation, visual guidance, target tracking, interceptor guidance, GNSS-free, computer vision navigation",
    "audience": {
      "@type": "Audience",
      "audienceType": "Defence contractors, UAV manufacturers, Autonomous vehicle developers"
    },
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "GBP",
      "priceSpecification": {
        "@type": "PriceSpecification",
        "price": "Contact for pricing"
      }
    }
  },
  '/platforms/helais': {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Helais Mission Orchestration Platform",
    "alternateName": ["Helais", "Helais Platform", "Helais Sentinel"],
    "description": "Drone fleet management and mission orchestration platform for security, emergency response, and aerial operations. Multi-drone coordination, dispatch integration, incident workflow, and asset management for enterprise UAV operations.",
    "brand": {
      "@type": "Brand",
      "name": "The RLK Group"
    },
    "manufacturer": {
      "@type": "Organization",
      "name": "The RLK Group",
      "url": BASE_URL
    },
    "category": "Mission Orchestration Platforms",
    "additionalType": "https://schema.org/SoftwareApplication",
    "applicationCategory": "Drone Fleet Management Software",
    "url": `${BASE_URL}/platforms/helais`,
    "image": "https://customer-assets.emergentagent.com/job_rlk-modern/artifacts/wepugaac_RLK%20Grey%20group.png",
    "keywords": "drone fleet management, mission orchestration, UAV coordination, drone dispatch, aerial operations, multi-drone control, enterprise drone platform, DaaS",
    "audience": {
      "@type": "Audience",
      "audienceType": "Emergency services, Security companies, Critical infrastructure operators"
    },
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "GBP",
      "priceSpecification": {
        "@type": "PriceSpecification",
        "price": "Contact for pricing"
      }
    }
  }
};

// Service schemas for solutions - Enhanced for better SEO
const serviceSchemas = {
  '/solutions/defence-cuas': {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Counter-UAS Capability Assessment & Defence Solutions",
    "alternateName": ["C-UAS Solutions", "Counter Drone Solutions", "Drone Defence"],
    "description": "Counter-UAS capability assessment, mission integration, and autonomous interceptor technology for defence programmes. UAVSimNet simulation for C-UAS evaluation, hardware-neutral mission systems.",
    "provider": {
      "@type": "Organization",
      "name": "The RLK Group",
      "url": BASE_URL
    },
    "serviceType": "Counter-UAS Technology Services",
    "areaServed": {
      "@type": "Country",
      "name": "United Kingdom"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Counter-UAS Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Counter-UAS Capability Assessment"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Mission Integration"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Autonomous Interceptor Technology"
          }
        }
      ]
    }
  },
  '/solutions/protective-security': {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Drone Security for Critical Infrastructure - Helais Sentinel",
    "alternateName": ["Helais Sentinel", "Drone Perimeter Security", "Aerial Site Protection"],
    "description": "Drone security system for critical infrastructure and site protection. Aerial observation integrates with existing CCTV and perimeter security systems. Operator-controlled response.",
    "provider": {
      "@type": "Organization",
      "name": "The RLK Group",
      "url": BASE_URL
    },
    "serviceType": "Protective Security Services",
    "areaServed": {
      "@type": "Country",
      "name": "United Kingdom"
    }
  },
  '/solutions/public-safety': {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Drone Technology for Emergency Services",
    "alternateName": ["Public Safety Drones", "Emergency Response UAV", "Drone as First Responder"],
    "description": "Mission orchestration platform for police, fire and emergency response. Multi-drone coordination, incident workflow integration, and BVLOS-ready aerial support systems.",
    "provider": {
      "@type": "Organization",
      "name": "The RLK Group",
      "url": BASE_URL
    },
    "serviceType": "Public Safety Technology Services",
    "areaServed": {
      "@type": "Country",
      "name": "United Kingdom"
    }
  },
  '/solutions/civil-intelligence': {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Video Analytics & Operational Intelligence",
    "alternateName": ["UrbanAnalytix", "Smart City Analytics", "CCTV Analytics"],
    "description": "AI video analytics for transport, retail, and public spaces. Convert existing CCTV into operational intelligence with footfall analytics, queue monitoring, and crowd density alerts.",
    "provider": {
      "@type": "Organization",
      "name": "The RLK Group",
      "url": BASE_URL
    },
    "serviceType": "Operational Analytics Services",
    "areaServed": {
      "@type": "Country",
      "name": "United Kingdom"
    }
  }
};

export const SEOHead = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  
  // Get SEO data for current page, fallback to homepage
  const pageSEO = seoData[currentPath] || seoData['/'];
  const canonicalUrl = `${BASE_URL}${currentPath === '/' ? '' : currentPath}`;
  
  useEffect(() => {
    // Update document title
    document.title = pageSEO.title;
    
    // Helper to update or create meta tag
    const setMeta = (name, content, property = false) => {
      if (!content) return;
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    // Helper to update or create link tag
    const setLink = (rel, href, attrs = {}) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
      Object.entries(attrs).forEach(([key, value]) => {
        link.setAttribute(key, value);
      });
    };
    
    // Basic meta tags
    setMeta('description', pageSEO.description);
    setMeta('keywords', pageSEO.keywords);
    setMeta('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    setMeta('author', 'The RLK Group');
    setMeta('language', 'en-GB');
    setMeta('revisit-after', '7 days');
    
    // Geo tags
    setMeta('geo.region', 'GB-SCT');
    setMeta('geo.placename', 'Glasgow');
    setMeta('geo.position', '55.8642;-4.2518');
    setMeta('ICBM', '55.8642, -4.2518');
    
    // Open Graph tags
    setMeta('og:title', pageSEO.title, true);
    setMeta('og:description', pageSEO.description, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('og:type', pageSEO.ogType, true);
    setMeta('og:site_name', 'The RLK Group', true);
    setMeta('og:locale', 'en_GB', true);
    setMeta('og:image', 'https://customer-assets.emergentagent.com/job_rlk-modern/artifacts/wepugaac_RLK%20Grey%20group.png', true);
    setMeta('og:image:alt', 'The RLK Group Logo', true);
    setMeta('og:image:width', '1200', true);
    setMeta('og:image:height', '630', true);
    
    // Twitter Card tags
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', pageSEO.title);
    setMeta('twitter:description', pageSEO.description);
    setMeta('twitter:image', 'https://customer-assets.emergentagent.com/job_rlk-modern/artifacts/wepugaac_RLK%20Grey%20group.png');
    setMeta('twitter:image:alt', 'The RLK Group Logo');
    
    // Canonical URL
    setLink('canonical', canonicalUrl);
    
    // Alternate language (if needed in future)
    setLink('alternate', canonicalUrl, { hreflang: 'en-gb' });
    setLink('alternate', canonicalUrl, { hreflang: 'x-default' });
    
    // Helper to set JSON-LD script
    const setJsonLd = (id, data) => {
      let script = document.getElementById(id);
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
    };
    
    // Organization schema
    setJsonLd('org-schema', organizationSchema);
    
    // Website schema
    setJsonLd('site-schema', websiteSchema);
    
    // Page-specific schema
    const pageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": pageSEO.title,
      "description": pageSEO.description,
      "url": canonicalUrl,
      "inLanguage": "en-GB",
      "isPartOf": {
        "@type": "WebSite",
        "name": "The RLK Group",
        "url": BASE_URL
      },
      "about": {
        "@type": "Organization",
        "name": "The RLK Group"
      },
      "dateModified": new Date().toISOString().split('T')[0]
    };
    setJsonLd('page-schema', pageSchema);
    
    // Product schema for product pages
    if (productSchemas[currentPath]) {
      setJsonLd('product-schema', productSchemas[currentPath]);
    } else {
      const existingProductScript = document.getElementById('product-schema');
      if (existingProductScript) existingProductScript.remove();
    }
    
    // Service schema for solution pages
    if (serviceSchemas[currentPath]) {
      setJsonLd('service-schema', serviceSchemas[currentPath]);
    } else {
      const existingServiceScript = document.getElementById('service-schema');
      if (existingServiceScript) existingServiceScript.remove();
    }
    
    // Breadcrumb schema for non-homepage
    if (currentPath !== '/') {
      const pathParts = currentPath.split('/').filter(Boolean);
      const breadcrumbItems = [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": BASE_URL
        }
      ];
      
      // Map path segments to readable names
      const nameMap = {
        'solutions': 'Solutions',
        'platforms': 'Platforms & Technology',
        'company': 'Company',
        'evidence': 'Evidence & Assurance',
        'insights': 'Insights',
        'contact': 'Contact',
        'defence-cuas': 'Defence & Counter-UAS',
        'protective-security': 'Protective Security',
        'public-safety': 'Public Safety',
        'civil-intelligence': 'Civil Intelligence',
        'ai-av': 'AI-AV',
        'helais': 'Helais',
        'advanced-development': 'Advanced Development',
        'structure': 'Our Companies',
        'intellectual-property': 'Intellectual Property'
      };
      
      let cumulativePath = '';
      pathParts.forEach((part, index) => {
        cumulativePath += '/' + part;
        const name = nameMap[part] || part.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        breadcrumbItems.push({
          "@type": "ListItem",
          "position": index + 2,
          "name": name,
          "item": `${BASE_URL}${cumulativePath}`
        });
      });
      
      const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbItems
      };
      setJsonLd('breadcrumb-schema', breadcrumbSchema);
    } else {
      const existingBreadcrumbScript = document.getElementById('breadcrumb-schema');
      if (existingBreadcrumbScript) existingBreadcrumbScript.remove();
    }
    
    // FAQ Schema for Evidence page
    if (currentPath === '/evidence') {
      const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What evidence does RLK provide with its products?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Every RLK product comes with defined evidence including test conditions, performance data, model provenance, confidence levels, and clear limitation statements."
            }
          },
          {
            "@type": "Question",
            "name": "How does RLK distinguish between development and production capabilities?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "RLK uses clear maturity indicators: Research (algorithm development, simulation), Development (sub-system testing, integration), and Production (system validation, operational deployment)."
            }
          }
        ]
      };
      setJsonLd('faq-schema', faqSchema);
    } else {
      const existingFaqScript = document.getElementById('faq-schema');
      if (existingFaqScript) existingFaqScript.remove();
    }
    
    // Contact page schema
    if (currentPath === '/contact') {
      const contactSchema = {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "name": "Contact The RLK Group",
        "description": "Contact The RLK Group to discuss operational requirements, platform integration, or partnership opportunities.",
        "url": `${BASE_URL}/contact`,
        "mainEntity": {
          "@type": "Organization",
          "name": "The RLK Group",
          "telephone": "+44-141-319-4813",
          "email": "info@rlktech.co.uk"
        }
      };
      setJsonLd('contact-schema', contactSchema);
    } else {
      const existingContactScript = document.getElementById('contact-schema');
      if (existingContactScript) existingContactScript.remove();
    }
    
  }, [currentPath, pageSEO, canonicalUrl]);
  
  return null;
};

export default SEOHead;
